import React, { useEffect, useState } from 'react';
import { Grid, TextField, Button, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dialogStyles from '../../../../components/dialog/GenericDialog.styles';
import ValidatedField from '../../../../components/inputs/ValidatedField';
import useAuthUser from '../../../auth/hooks/useAuthUser';
import { formatName, generateFileName, toInputDate } from '../../utils/unit.helpers';
import { fetchOperatorsLite, refranchiseOperator } from '../../../operator/services/operator.api';
import { UNIT_DATE_RULES } from '../../utils/unitValidation';

export default function RefranchiseUnitTab({ initialData, onSubmit, helpers }) {
  const { close, setLoading } = helpers;
  const navigate = useNavigate();

  const { user } = useAuthUser();
  const displayUser = formatName(user?.name) || user?.email || 'Unknown User';

  const [operators, setOperators] = useState([]);
  const [selectedNewOperator, setSelectedNewOperator] = useState('');
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('');

  useEffect(() => {
    let mounted = true;
    fetchOperatorsLite()
      .then((ops) => mounted && setOperators(ops))
      .catch(() => mounted && setOperators([]));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setSelectedNewOperator('');
    setNewEffectiveFrom('');
    setLoading(false);
  }, [setLoading, initialData?.name]);

  const validDate = UNIT_DATE_RULES.every((r) => r.test(newEffectiveFrom));
  const canSave = Boolean(selectedNewOperator) && validDate;

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    const filename = generateFileName();
    try {
      await refranchiseOperator({
        newOperator: selectedNewOperator,
        effectiveFrom: newEffectiveFrom,
        units: [initialData?.name],
        filename,
        user: displayUser,
      });
      onSubmit?.();
      close();
      navigate('/home/structure-logs', { state: { fileName: filename, status: null } });
    } catch (err) {
      alert('Failed to refranchise unit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Unit Name"
            fullWidth
            size="small"
            variant="standard"
            value={initialData?.name || ''}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Old Operator"
            fullWidth
            size="small"
            variant="standard"
            value={initialData?.operator || ''}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="New Operator"
            select
            fullWidth
            size="small"
            variant="outlined"
            value={selectedNewOperator}
            onChange={(e) => setSelectedNewOperator(e.target.value)}
            sx={dialogStyles.outlinedField(Boolean(selectedNewOperator))}
          >
            <MenuItem value="" disabled>
              Select new operator
            </MenuItem>
            {operators.map((op) => (
              <MenuItem key={op.code} value={op.code}>
                {op.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Old Effective From"
            fullWidth
            size="small"
            type="date"
            variant="standard"
            value={toInputDate(initialData?.effectiveFrom)}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="New Effective From"
            type="date"
            value={newEffectiveFrom}
            onChange={(e) => setNewEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            rules={UNIT_DATE_RULES}
          />
        </Grid>
      </Grid>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <Button onClick={close} variant="contained" sx={dialogStyles.btnSecondary}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          sx={dialogStyles.btnPrimary}
        >
          Save Changes
        </Button>
      </div>
    </>
  );
}